import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
  Chip,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import LoadingPage from "./LoadingPage";

const EditGroup = () => {
  const { accessToken, isAdmin, isSuperuser } = useAuth();
  const { id } = useParams(); // Get groupId from URL
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_BASEURL;

  // State
  const [siteName, setSiteName] = useState("");
  const [groupName, setGroupName] = useState("");
  const [devices, setDevices] = useState([]);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [loadingGroup, setLoadingGroup] = useState(true);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");

  // Fetch group and devices data
  useEffect(() => {
    if (!id) {
      setError("Invalid group ID. Please select a valid group.");
      setLoadingGroup(false);
      setLoadingDevices(false);
      return;
    }

    if (!isAdmin && !isSuperuser) {
      setError("You do not have permission to edit groups.");
      setLoadingGroup(false);
      setLoadingDevices(false);
      return;
    }

    const fetchData = async () => {
      setLoadingGroup(true);
      setLoadingDevices(true);
      setError(null);

      try {
          
        // Fetch group data
        const groupResponse = await axios.get(`${baseURL}/groups/${id}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
            });
            const groupData = groupResponse.data;
            console.log(groupData)
            setGroupName(groupData.name || "");
              
        // Fetch site data
        const sitesResponse = await axios.get(`${baseURL}/get-sites`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        console.log(sitesResponse.data.data);
        const siteMap = new Map(sitesResponse.data.data.map(site => [String(site.id), site.name || `Site ${site.id}`]));
        console.log(siteMap);
        setSiteName(siteMap.get(String(groupData.site)) || `Site ${groupData.site}`);

        // Fetch all groups
        const groupsResponse = await axios.get(`${baseURL}/groups`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        });
        const allGroups = groupsResponse.data || [];

        // Fetch available devices
        const devicesResponse = await axios.get(`${baseURL}/devices`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        console.log(devicesResponse.data)
        const deviceList = devicesResponse.data || [];

        // Filter devices by type, site, and availability
        const filteredDevices = deviceList.filter(device => {
            // Check device_type is "hydra"
            const isHydra = device.device_type === "hydra";
            // Check device belongs to the group's site
            const matchesSite = String(device.site) === String(groupData.site);
            return isHydra && matchesSite;
        });
        setAvailableDevices(filteredDevices);
        console.log(filteredDevices);

        const hydraDevices = (groupData.devices || []).filter(deviceId => {
        const device = deviceList.find(d => d.id === deviceId);
        return device && device.device_type === "hydra" && String(device.site) === String(groupData.site);
        });
        setDevices(hydraDevices);
      } catch (err) {
        if (err.response) {
          setError(err.response.data.message || "Failed to load group or devices. Please try again.");
        } else if (err.request) {
          setError("No response from server. Please try again.");
        } else {
          setError("An unexpected error occurred. Please try again.");
        }
      } finally {
        setLoadingGroup(false);
        setLoadingDevices(false);
      }
    };
    fetchData();
  }, [accessToken, baseURL, id, isAdmin, isSuperuser]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    // Validate inputs
    if (!groupName.trim()) {
      setError("Group name is required.");
      return;
    }

    // Open confirmation dialog for update
    setDialogAction("update");
    setDialogMessage(`Are you sure you want to update the group "${groupName}"?`);
    setDialogOpen(true);
  };

  // Perform the update action
  const performUpdate = async () => {
    setLoadingSubmit(true);
    try {
      const response = await axios.patch(
        `${baseURL}/groups/${id}`,
        {
          name: groupName,
          devices: devices,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      setSuccess("Group updated successfully!");
      setDevices(response.data.devices || []); // Update devices in case API modifies them
    } catch (err) {
      if (err.response) {
        setError(err.response.data.message || "Failed to update group. Please try again.");
      } else if (err.request) {
        setError("No response from server. Please try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setLoadingSubmit(false);
    }
  };

  // Handle group deletion
  const handleDelete = () => {
    setError(null);
    setSuccess(null);
    // Open confirmation dialog for delete
    setDialogAction("delete");
    setDialogMessage(`Are you sure you want to delete the group "${groupName}"? This action cannot be undone.`);
    setDialogOpen(true);
  };

  // Handle dialog confirmation
  const handleConfirmAction = async () => {
    setDialogOpen(false);
    if (dialogAction === "update") {
      await performUpdate();
    } else if (dialogAction === "delete") {
      setLoadingDelete(true);
      try {
        await axios.delete(`${baseURL}/groups/${id}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            accept: "application/json",
          },
        });

        setSuccess("Group deleted successfully!");
        setTimeout(() => navigate("/groups"), 1500); // Redirect after brief success message
      } catch (err) {
        if (err.response) {
          setError(err.response.data.message || "Failed to delete group. Please try again.");
        } else if (err.request) {
          setError("No response from server. Please try again.");
        } else {
          setError("An unexpected error occurred. Please try again.");
        }
      } finally {
        setLoadingDelete(false);
      }
    }
  };

  // Handle dialog cancellation
  const handleCancelAction = () => {
    setDialogOpen(false);
    setDialogAction("");
    setDialogMessage("");
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom>
        <Skeleton width="30%" height={40} />
      </Typography>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" width={120} height={40} />
      </Paper>
    </Container>
  );

  // Redirect or show error if id is invalid or no permission
  if (!id || (!isAdmin && !isSuperuser)) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
        <Alert severity="error">
          {id ? "You do not have permission to edit groups." : "Invalid group ID. Please select a valid group or return to the group list."}
        </Alert>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate("/groups")}
          sx={{ mt: 2 }}
        >
          Back to Groups
        </Button>
      </Container>
    );
  }

//   if (loadingGroup || loadingDevices) {
//     return <LoadingSkeleton />;
//   }
  if (loadingGroup || loadingDevices) {
    return <LoadingPage />;
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
      <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: "1.75rem", md: "2.125rem" } }}>
        Edit Group
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Site Name - READ ONLY */}
            <TextField
                label="Site"
                value={siteName}
                fullWidth
                variant="outlined"
                disabled
                sx={{ "& .MuiInputLabel-root": { color: "black", fontWeight: 100 } }}
            />
          {/* Group Name */}
          <TextField
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
            required
            variant="outlined"
            sx={{ "& .MuiInputLabel-root": { color: "black", fontWeight: 100 } }}
          />
          {/* Devices */}
            <FormControl fullWidth variant="outlined">
                <InputLabel id="devices-label" sx={{ color: "black", fontWeight: 100 }}>
                    Devices
                </InputLabel>
                <Select
                    labelId="devices-label"
                    multiple
                    value={devices}
                    onChange={(e) => setDevices(e.target.value)}
                    label="Devices"
                    renderValue={(selected) => (
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((id) => {
                                const device = availableDevices.find((d) => d.id === id);
                                const name = device ? device.name || `Device ${id}` : id;
                                return <Chip key={id} label={name} size="medium" variant="outlined" deleteIcon={<DeleteIcon />}  onDelete={() => setDevices((prev) => prev.filter((d) => d !== id))}
                                          sx={{
                                                backgroundColor: "transparent",
                                                color: "#f5c518",
                                                fontSize: '16px',
                                                fontWeight: 600,
                                                border: "1px solid #f5c518",
                                                borderRadius: '5px',
                                                "& .MuiChip-deleteIcon": {
                                                color: "#d32f2f", // delete icon color
                                                },
                                                "&:hover": {
                                                backgroundColor: "#d32f2f",
                                                color: "white",
                                                border: "1px solid red",
                                                "& .MuiChip-deleteIcon": {
                                                color: "white", // delete icon color
                                                },
                                                },
                                            }}
                                />;
                            })}
                        </Box>
                    )}
                >
                    {availableDevices.length === 0 ? (
                        <MenuItem value="" disabled>
                            No devices available
                        </MenuItem>
                    ) : (
                        availableDevices.map((device) => (
                            <MenuItem key={device.id} value={device.id}>
                                {device.name || `Device ${device.id}`}
                            </MenuItem>
                        ))
                    )}
                </Select>
            </FormControl>
          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, mt: 2, alignSelf: "flex-end" }}>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={loadingDelete || loadingSubmit || loadingGroup || loadingDevices}
              sx={{ minWidth: 120 }}
            >
              {loadingDelete ? <CircularProgress size={24} /> : "Delete Group"}
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loadingSubmit || loadingDelete || loadingGroup || loadingDevices}
              sx={{ minWidth: 120 }}
            >
              {loadingSubmit ? <CircularProgress size={24} /> : "Update Group"}
            </Button>
          </Box>
        </Box>
      </Paper>
      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCancelAction}
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ color: "black" }}>
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction} color="primary">
            No
          </Button>
          <Button onClick={handleConfirmAction} color="error" autoFocus>
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditGroup;